<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import whatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const templates = ref([]);
const lastUpdatedAt = ref(null);
const isLoading = ref(false);
const isSyncing = ref(false);
const isCreating = ref(false);
const isUpdating = ref(false);
const templateToEdit = ref(null);
const templateToDelete = ref(null);

const form = reactive({
  name: '',
  category: 'UTILITY',
  language: 'en_US',
  body: '',
  examples: '',
});

const categoryOptions = ['UTILITY', 'MARKETING'];
const languageOptions = ['en_US', 'pt_BR', 'es_ES'];

const isEditing = computed(() => !!templateToEdit.value);

const formTitle = computed(() =>
  isEditing.value
    ? t('INBOX_MGMT.WHATSAPP_TEMPLATES.EDIT_TITLE')
    : t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE_TITLE')
);

const submitLabel = computed(() =>
  isEditing.value
    ? t('INBOX_MGMT.WHATSAPP_TEMPLATES.UPDATE')
    : t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE')
);

const formattedLastUpdatedAt = computed(() => {
  if (!lastUpdatedAt.value)
    return t('INBOX_MGMT.WHATSAPP_TEMPLATES.NEVER_SYNCED');
  return new Date(lastUpdatedAt.value).toLocaleString();
});

const deleteConfirmMessage = computed(() =>
  t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM.MESSAGE', {
    name: templateToDelete.value?.name || '',
  })
);

const deleteConfirmText = computed(() =>
  t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM.YES', {
    name: templateToDelete.value?.name || '',
  })
);

const templateVariables = computed(() => {
  const matches = form.body.match(/{{\s*\d+\s*}}/g) || [];
  return matches.length;
});

const statusClass = status => {
  const normalizedStatus = status?.toUpperCase();
  if (normalizedStatus === 'APPROVED') return 'bg-n-teal-3 text-n-teal-11';
  if (normalizedStatus === 'PENDING') return 'bg-n-amber-3 text-n-amber-11';
  if (normalizedStatus === 'REJECTED') return 'bg-n-ruby-3 text-n-ruby-11';
  if (normalizedStatus === 'PAUSED') return 'bg-n-orange-3 text-n-orange-11';
  return 'bg-n-slate-3 text-n-slate-11';
};

const isTemplatePending = template =>
  template.status?.toUpperCase() === 'PENDING';

const bodyComponentFor = template =>
  Array(template.components).find(component => component.type === 'BODY') || {};

const bodyExamplesFor = template => {
  const bodyText = bodyComponentFor(template).example?.body_text || [];
  return Array(bodyText[0] || []).join(', ');
};

const errorMessage = error =>
  error?.response?.data?.error || t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.ERROR');

const fetchTemplates = async () => {
  isLoading.value = true;
  try {
    const { data } = await whatsappTemplatesAPI.index(props.inbox.id);
    templates.value = data.templates || [];
    lastUpdatedAt.value = data.last_updated_at;
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLoading.value = false;
  }
};

const syncTemplates = async () => {
  isSyncing.value = true;
  try {
    const { data } = await whatsappTemplatesAPI.sync(props.inbox.id);
    templates.value = data.templates || [];
    lastUpdatedAt.value = data.last_updated_at;
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.SYNC_SUCCESS'));
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSyncing.value = false;
  }
};

const buildComponents = () => {
  const bodyComponent = {
    type: 'BODY',
    text: form.body,
  };

  if (templateVariables.value) {
    bodyComponent.example = {
      body_text: [form.examples.split(',').map(example => example.trim())],
    };
  }

  return [bodyComponent];
};

const resetForm = () => {
  templateToEdit.value = null;
  form.name = '';
  form.category = 'UTILITY';
  form.language = 'en_US';
  form.body = '';
  form.examples = '';
};

const openEdit = template => {
  if (isTemplatePending(template)) {
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.PENDING_EDIT_DISABLED'));
    return;
  }

  const bodyComponent = bodyComponentFor(template);
  templateToEdit.value = template;
  form.name = template.name;
  form.category = template.category;
  form.language = template.language;
  form.body = bodyComponent.text || '';
  form.examples = bodyExamplesFor(template);
};

const updateTemplate = async () => {
  isUpdating.value = true;
  try {
    await whatsappTemplatesAPI.update(props.inbox.id, {
      name: templateToEdit.value.name,
      category: form.category,
      language: form.language,
      components: buildComponents(),
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.UPDATE_SUCCESS'));
    resetForm();
    await fetchTemplates();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isUpdating.value = false;
  }
};

const createTemplate = async () => {
  isCreating.value = true;
  try {
    await whatsappTemplatesAPI.create(props.inbox.id, {
      name: form.name,
      category: form.category,
      language: form.language,
      components: buildComponents(),
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.CREATE_SUCCESS'));
    resetForm();
    await fetchTemplates();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isCreating.value = false;
  }
};

const submitTemplate = () => {
  return isEditing.value ? updateTemplate() : createTemplate();
};

const openDelete = template => {
  templateToDelete.value = template;
};

const closeDelete = () => {
  templateToDelete.value = null;
};

const confirmDelete = async () => {
  if (!templateToDelete.value) return;

  try {
    await whatsappTemplatesAPI.deleteTemplate(
      props.inbox.id,
      templateToDelete.value
    );
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.DELETE_SUCCESS'));
    closeDelete();
    await fetchTemplates();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

onMounted(fetchTemplates);
</script>

<template>
  <div class="mx-6 max-w-7xl space-y-6">
    <section class="rounded-xl border border-n-weak bg-n-solid-1 p-6">
      <div
        class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between"
      >
        <div>
          <h3 class="flex items-center gap-1 text-heading-3 text-n-slate-12">
            <span>{{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TITLE') }}</span>
            <fluent-icon
              v-tooltip.top="
                $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREDENTIALS_TOOLTIP')
              "
              icon="info"
              size="14"
              class="text-n-slate-11"
            />
          </h3>
          <p class="mt-1 text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DESCRIPTION') }}
          </p>
          <p class="mt-2 text-body-mini text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LAST_SYNCED') }}
            {{ formattedLastUpdatedAt }}
          </p>
        </div>
        <NextButton
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC')"
          :is-loading="isSyncing"
          :disabled="isSyncing"
          size="sm"
          @click="syncTemplates"
        />
      </div>
    </section>

    <section class="rounded-xl border border-n-weak bg-n-solid-1 p-6">
      <h4 class="text-heading-4 text-n-slate-12">
        {{ formTitle }}
      </h4>
      <form
        class="mt-4 grid gap-4 md:grid-cols-2"
        @submit.prevent="submitTemplate"
      >
        <label class="flex flex-col gap-1 text-body-main text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.NAME') }}
          <input
            v-model="form.name"
            required
            pattern="[a-z0-9_]+"
            :disabled="isEditing"
            class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.PLACEHOLDERS.NAME')"
          />
        </label>

        <label class="flex flex-col gap-1 text-body-main text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.LANGUAGE') }}
          <select
            v-model="form.language"
            :disabled="isEditing"
            class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option
              v-for="language in languageOptions"
              :key="language"
              :value="language"
            >
              {{ language }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-body-main text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.CATEGORY') }}
          <select
            v-model="form.category"
            class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option
              v-for="category in categoryOptions"
              :key="category"
              :value="category"
            >
              {{ category }}
            </option>
          </select>
        </label>

        <label
          class="flex flex-col gap-1 text-body-main text-n-slate-12 md:col-span-2"
        >
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.BODY') }}
          <textarea
            v-model="form.body"
            required
            rows="4"
            class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.PLACEHOLDERS.BODY')"
          />
        </label>

        <label
          class="flex flex-col gap-1 text-body-main text-n-slate-12 md:col-span-2"
        >
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.EXAMPLES') }}
          <input
            v-model="form.examples"
            :required="templateVariables > 0"
            class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.PLACEHOLDERS.EXAMPLES')
            "
          />
          <span class="text-body-mini text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EXAMPLES_HELP') }}
          </span>
        </label>

        <div class="md:col-span-2 flex justify-end gap-2">
          <NextButton
            v-if="isEditing"
            type="button"
            :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CANCEL_EDIT')"
            ghost
            @click="resetForm"
          />
          <NextButton
            type="submit"
            :label="submitLabel"
            :is-loading="isCreating || isUpdating"
            :disabled="isCreating || isUpdating"
          />
        </div>
      </form>
    </section>

    <section class="rounded-xl border border-n-weak bg-n-solid-1 p-6">
      <div
        v-if="isLoading"
        class="py-10 text-center text-body-main text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LOADING') }}
      </div>
      <div
        v-else-if="!templates.length"
        class="py-10 text-center text-body-main text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY') }}
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full min-w-[48rem] text-left text-body-main">
          <thead class="border-b border-n-weak text-n-slate-11">
            <tr>
              <th class="py-3 pr-4 font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.NAME') }}
              </th>
              <th class="py-3 pr-4 font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.LANGUAGE') }}
              </th>
              <th class="py-3 pr-4 font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.CATEGORY') }}
              </th>
              <th class="py-3 pr-4 font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.STATUS') }}
              </th>
              <th class="py-3 pr-4 font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="template in templates"
              :key="`${template.name}-${template.language}`"
            >
              <td class="py-3 pr-4 text-n-slate-12">
                {{ template.name }}
              </td>
              <td class="py-3 pr-4 text-n-slate-11">
                {{ template.language }}
              </td>
              <td class="py-3 pr-4 text-n-slate-11">
                {{ template.category }}
              </td>
              <td class="py-3 pr-4">
                <span
                  class="rounded-full px-2 py-1 text-body-mini font-medium"
                  :class="statusClass(template.status)"
                >
                  {{
                    template.status ||
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.UNKNOWN_STATUS')
                  }}
                </span>
              </td>
              <td class="py-3 pr-4">
                <div class="flex items-center gap-2">
                  <span
                    v-tooltip.top="
                      isTemplatePending(template)
                        ? $t(
                            'INBOX_MGMT.WHATSAPP_TEMPLATES.PENDING_EDIT_DISABLED'
                          )
                        : ''
                    "
                  >
                    <NextButton
                      :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.EDIT')"
                      ghost
                      size="sm"
                      :disabled="isTemplatePending(template)"
                      @click="openEdit(template)"
                    />
                  </span>
                  <NextButton
                    :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE')"
                    ghost
                    ruby
                    size="sm"
                    @click="openDelete(template)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <woot-confirm-delete-modal
      v-if="templateToDelete"
      :show="!!templateToDelete"
      :title="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM.TITLE')"
      :message="deleteConfirmMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM.NO')"
      :confirm-value="templateToDelete.name"
      :confirm-place-holder-text="
        $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM.PLACEHOLDER')
      "
      @on-confirm="confirmDelete"
      @on-close="closeDelete"
    />
  </div>
</template>
